// ============================================================================
//  catalogueCache — the reference lists an open claim needs, fetched once
// ============================================================================
//
//  Opening a claim fired ten requests. Four of them are CATALOGUES — every
//  procedure, diagnosis, investigation and medicine priced for the claim's
//  scheme — and they are the heavy ones:
//
//      procedures     145 rows    53 KB
//      diagnoses      477 rows   103 KB
//      investigations  47 rows     5 KB
//      medicines      446 rows   114 KB
//
//  None of them depend on the CLAIM. They depend on its scheme, and two of them
//  on the patient's age band. Stepping Next through a month re-fetched all four
//  for every claim, because the editor is rendered inside the open row: moving to
//  another row unmounts it and mounts a new one, and a fresh mount re-runs the
//  effect no matter what its dependency array says.
//
//  So the cache lives HERE, at module scope, not in component state — that is the
//  only place that survives the unmount. Stepping from one claim to the next in
//  the same scheme now costs the claim's own detail and nothing else.
//
//  TWO THINGS THIS ALSO FIXES
//
//  · procedureDates was requested three times per claim — Procedures,
//    Investigations and Medications each asked for the same visit's days. Three
//    identical in-flight requests now share one.
//
//  · A slow catalogue used to be requested again by the next claim before the
//    first had answered. In-flight requests are shared, so N mounts asking for
//    the same thing make one request between them.
//
//  STALENESS. A tariff edited in the admin panel must not be invisible here for
//  the rest of the session, so entries expire. Five minutes: long enough that
//  stepping through a month never re-fetches, short enough that an admin change
//  shows up while someone is still on the phone about it. `clear()` is exported
//  for a deliberate refresh.
import axios from 'axios';

//  key -> { at: epoch ms, value }   for answered requests
const answered = new Map();
//  key -> Promise                   for requests still in the air
const inFlight = new Map();

export const CATALOGUE_TTL_MS = 5 * 60 * 1000;

//  Params are sorted into the key so { scheme, age } and { age, scheme } are one
//  entry rather than two. Undefined and null are dropped, because axios omits
//  them from the query string and they must not split the key either.
const keyOf = (url, params) => {
  const flat = Object.entries(params || {})
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
  return `${url}?${flat}`;
};

//  A GET whose answer is worth keeping. Returns the response DATA, not the axios
//  response — nothing here needs the headers, and holding the whole response
//  keeps its config and request objects alive for the life of the cache.
//  ttl: 0 means DEDUPE ONLY — share a request that is already in the air, but
//  never answer from a stored one. That is what the visit-date lookup needs: the
//  three tables ask for it in the same commit, so one request should serve all
//  three, but it is ALSO re-requested after a save, because extending the stay
//  adds days. Storing it would hand that reload the list from before the save.
export function cachedGet(url, params, { ttl = CATALOGUE_TTL_MS } = {}) {
  const key = keyOf(url, params);
  const now = Date.now();

  if (ttl > 0) {
    const hit = answered.get(key);
    if (hit && now - hit.at < ttl) return Promise.resolve(hit.value);
  }

  const pending = inFlight.get(key);
  if (pending) return pending;

  const p = axios.get(url, { params })
    .then(({ data }) => {
      if (ttl > 0) answered.set(key, { at: Date.now(), value: data });
      inFlight.delete(key);
      return data;
    })
    .catch((err) => {
      //  A failure is NOT cached. The next claim asks again, which is what you
      //  want when the cause was a dropped connection — caching the failure
      //  would keep the list empty until the TTL expired.
      inFlight.delete(key);
      throw err;
    });

  inFlight.set(key, p);
  return p;
}

//  Everything, or just the entries whose key contains `match`. Called after an
//  edit that could change what the catalogues hold.
export function clearCatalogueCache(match) {
  if (!match) { answered.clear(); inFlight.clear(); return; }
  for (const k of [...answered.keys()]) if (k.includes(match)) answered.delete(k);
  for (const k of [...inFlight.keys()]) if (k.includes(match)) inFlight.delete(k);
}

//  For the tests: what is actually being held.
export function cacheStats() {
  return { answered: answered.size, inFlight: inFlight.size };
}
